// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file Banner.tsx
 * @input Uses React useState/useRef/useId, Button, Icon (with registry string names), StyleX
 * @output Exports Banner component, BannerProps, BannerStatus, BannerContainer types
 * @position Core implementation; consumed by index.ts, tested by Banner.test.tsx
 *
 * Visual structure:
 * - Root container: layout-only wrapper (flex column), no visual styling, no theme target
 * - Header area (themeProps 'banner'): colored status background with icon, title, description, actions, dismiss
 * - Content area (themeProps 'banner-content'): collapsible card background for additional content (children)
 * - Status icon (themeProps 'banner-icon'): the target rides on the default
 *   <Icon> itself — the element that paints — so 'status:X' overrides reach
 *   the glyph (#4166); for a custom `icon` node it stays on the layout wrapper
 * - No left border accent — color is expressed through the full header background
 * - Each visual area owns its own border-radius (no overflow:clip on the container)
 * - When children are provided, a collapse/expand toggle button appears in the end area
 *
 * A status added through `BannerStatusMap` augmentation has no entry in the
 * status lookups, so it renders with no status fill, no default glyph and the
 * polite `role="status"` rather than losing its ARIA role entirely.
 *
 * Title and description render as <div> (not <p>): they accept arbitrary
 * ReactNode content, and <p> cannot legally contain block-level children
 * (the HTML parser reparents them, desyncing SSR markup from the hydrated
 * DOM). Using <div> keeps these slots composable with any content. Their
 * StyleX styles set margin: 0 and explicit typography, so the rendered
 * appearance is identical to the previous <p>.
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/Banner/Banner.doc.mjs (props table, features, implementation notes)
 * - /packages/core/src/Banner/Banner.test.tsx (tests for new/changed behavior)
 * - /packages/core/src/Banner/index.ts (exports if types change)
 * - /apps/storybook/stories/Banner.stories.tsx (storybook stories)
 * - /packages/cli/assets/templates/blocks/components/Banner/ (showcase blocks)
 */
import { useId, useRef, useState } from 'react';
import * as stylex from '@stylexjs/stylex';
import { Button } from "../Button/index.js";
import { Icon } from "../Icon/index.js";
import "../theme/tokens.stylex.js";
import { colorVars, spacingVars, radiusVars, fontWeightVars, typeScaleVars, borderVars, durationVars, easeVars, shadowVars } from "../theme/tokens.stylex.js";
import { composeEventHandlers, isRenderable, mergeProps } from "../utils/index.js";
import { edgeCompSlot } from "../Layout/edgeCompensation.stylex.js";
import { themeProps } from "../utils/themeProps.js";
import { useTranslator } from "../i18n/index.js";

// =============================================================================
// Types
// =============================================================================

/**
 * Status type controlling the banner's icon and color.
 * Extensible via module augmentation of BannerStatusMap.
 */

/**
 * Container type of the banner.
 * - `card`: standalone card with border-radius and shadow
 * - `section`: full-width section banner (no border-radius)
 *
 * Extensible via module augmentation of BannerContainerMap.
 */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// =============================================================================
// Status lookups
// =============================================================================

// `BannerStatus` is `keyof BannerStatusMap`, and index.ts documents augmenting
// that interface to add a status. Every lookup below is therefore partial: an
// augmented status the library has never heard of falls through to the base
// treatment (no status fill, no glyph, the polite role) instead of resolving to
// `undefined` and dropping the ARIA role along with it.

const defaultIconNames = {
  info: 'info',
  warning: 'warning',
  error: 'error',
  success: 'success'
};
const statusRole = {
  info: 'status',
  warning: 'alert',
  error: 'alert',
  success: 'status'
};

/** An unknown status is not urgent by definition, so it announces politely. */
const FALLBACK_ROLE = 'status';
const statusIconColor = {
  info: 'accent',
  warning: 'warning',
  error: 'error',
  success: 'success'
};

// =============================================================================
// Styles
// =============================================================================

const styles = {
  root: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kMv6JI: "xjb2p0i",
    $$css: true
  },
  rootElevatedCard: {
    kaIpWk: "x1vf2byn",
    $$css: true
  },
  header: {
    k1xSpc: "x78zum5",
    kGNEyG: "x1cy8zhl",
    kOIVth: "x1txdalj",
    k8WAf4: "x8o8v82",
    kg3NbH: "x1pzlopt",
    $$css: true
  },
  headerCardStandalone: {
    kaIpWk: "x1vf2byn",
    $$css: true
  },
  headerCardWithContent: {
    krdFHd: "xwxb7qi",
    kfmiAY: "x13nour3",
    kVL7Gh: "xbiv7yw",
    kT0f0o: "x16uus16",
    $$css: true
  },
  headerCentered: {
    kGNEyG: "x6s0dn4",
    $$css: true
  },
  endArea: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kOIVth: "x1txdalj",
    kmuXW: "x2lah0s",
    keTefX: "xvc5jky",
    kqGvvJ: "x81ka23",
    $$css: true
  },
  chevron: {
    k1ekBW: "x11xpdln",
    kIyJzY: "xuedmi6 x12w9bfk",
    kAMwcw: "xlr8y92",
    $$css: true
  },
  chevronExpanded: {
    k3aq6I: "x19jd1h0",
    $$css: true
  }
};
const statusStyles = {
  info: {
    kWkggS: "xgcxg3y",
    $$css: true
  },
  warning: {
    kWkggS: "x24i8r5",
    $$css: true
  },
  error: {
    kWkggS: "x1pritpl",
    $$css: true
  },
  success: {
    kWkggS: "xu13z74",
    $$css: true
  }
};

/**
 * Narrows an augmented `BannerStatus` to one the style map actually carries,
 * so an unknown status renders with no status fill rather than crashing the
 * lookup. StyleX style maps cannot be declared `Partial`, hence the guard.
 */
function hasStatusStyle(status) {
  return Object.prototype.hasOwnProperty.call(statusStyles, status);
}

// Resting elevation for the banner. Applied to the root so the shadow wraps
// the whole banner (header + optional content). 'none' is the default and
// leaves the layout-only root untouched. For the `card` container the root is
// rounded (rootElevatedCard) so the shadow follows the card silhouette; the
// full-width `section` container stays square.
const elevationStyles = {
  none: {
    kGVxlE: "x1gnnqk1",
    $$css: true
  },
  low: {
    kGVxlE: "x1i5ehqx",
    $$css: true
  },
  med: {
    kGVxlE: "x14hfi27",
    $$css: true
  },
  high: {
    kGVxlE: "x1kcpxr7",
    $$css: true
  }
};

// =============================================================================
// Component
// =============================================================================

/**
 * A persistent status notification banner for info, warning, error, or success messages.
 *
 * Two-part visual structure:
 * - Header: colored status background with icon, title, description, and actions
 * - Content (optional): collapsible card background area for additional rich content
 *
 * When children are provided, a collapse/expand chevron button appears in the
 * header end area (to the left of the dismiss button if present). Clicking it
 * toggles the visibility of the content area.
 *
 * Manages its own dismissed state internally — the banner hides on dismiss
 * even if `onDismiss` is not provided, so product teams don't need to wire
 * up state management for basic dismiss behavior.
 *
 * Uses `role="alert"` for error/warning and `role="status"` for info/success.
 *
 * @example
 * ```
 * <Banner status="info" title="New update available" />
 * <Banner
 *   status="error"
 *   title="Something went wrong"
 *   description="Please try again later."
 *   isDismissable
 *   onDismiss={() => logDismiss()}
 * />
 * <Banner
 *   status="error"
 *   title="Multiple errors found"
 *   description="The following issues need to be resolved:"
 *   isDismissable>
 *   <ul>
 *     <li>Email address is invalid</li>
 *     <li>Password must be at least 8 characters</li>
 *   </ul>
 * </Banner>
 * <Banner
 *   status="warning"
 *   title="Configuration changes"
 *   defaultIsExpanded>
 *   <p>Details here...</p>
 * </Banner>
 * ```
 */
export function Banner({
  status,
  title,
  description,
  icon,
  isDismissable = false,
  onDismiss,
  endContent,
  container = 'card',
  elevation = 'none',
  defaultIsExpanded = false,
  children,
  xstyle,
  className,
  style,
  ref,
  onFocusCapture,
  onPointerDownCapture,
  ...rest
}) {
  const t = useTranslator();
  const [isDismissed, setIsDismissed] = useState(false);
  const [isExpanded, setIsExpanded] = useState(defaultIsExpanded);
  // The element focus came from before it entered the banner. Dismissing
  // unmounts the whole banner, dismiss button included, so without a handoff
  // the browser drops focus to <body> and a keyboard user loses their place.
  // ToastViewport makes the same handoff when a focused toast is dismissed.
  const focusOriginRef = useRef(null);
  // Links the expand/collapse toggle to the content region it shows/hides so
  // assistive tech can move from the button to its controlled content
  // (disclosure pattern). The region is conditionally rendered, so aria-controls
  // below is set only while it's mounted to avoid a dangling reference.
  const contentId = useId();
  const defaultIconName = defaultIconNames[status];
  const role = statusRole[status] ?? FALLBACK_ROLE;
  const iconColor = statusIconColor[status];
  const hasChildren = isRenderable(children);
  if (isDismissed) {
    return null;
  }

  // `focusin` reports the element focus came *from* as relatedTarget; on
  // pointerdown focus has not moved yet, so document.activeElement is it.
  const rememberFocusOrigin = (candidate, root) => {
    if (candidate instanceof HTMLElement && candidate !== document.body && !root.contains(candidate)) {
      focusOriginRef.current = candidate;
    }
  };
  const handleFocusCapture = event => {
    rememberFocusOrigin(event.relatedTarget, event.currentTarget);
  };
  const handlePointerDownCapture = event => {
    rememberFocusOrigin(document.activeElement, event.currentTarget);
  };
  const handleDismiss = () => {
    const origin = focusOriginRef.current;
    setIsDismissed(true);
    onDismiss?.();
    // Move focus before React removes the subtree, so the browser never has a
    // frame where the focused node is gone.
    if (origin?.isConnected) {
      origin.focus();
    }
  };
  const handleToggleExpand = () => {
    setIsExpanded(prev => !prev);
  };

  // Show the end area if there are actions, dismiss, or a collapsible toggle
  const showEndArea = isRenderable(endContent) || isDismissable || hasChildren;
  // Center items vertically when there's only a title (no description)
  // and the banner has action buttons
  const hasActions = isRenderable(endContent) || isDismissable;
  const isSingleLine = !isRenderable(description) && hasActions;
  const showContent = hasChildren && isExpanded;
  const isCard = container === 'card';
  return /*#__PURE__*/_jsxs("div", {
    ref: ref,
    role: role,
    onFocusCapture: composeEventHandlers(onFocusCapture, handleFocusCapture),
    onPointerDownCapture: composeEventHandlers(onPointerDownCapture, handlePointerDownCapture),
    ...mergeProps(stylex.props(styles.root, elevationStyles[elevation], isCard && elevation !== 'none' && styles.rootElevatedCard, xstyle), className, style),
    ...rest,
    children: [/*#__PURE__*/_jsxs("div", {
      ...mergeProps(themeProps('banner', {
        container,
        status
      }), stylex.props(styles.header, isSingleLine && styles.headerCentered, hasStatusStyle(status) && statusStyles[status], isCard && (showContent ? styles.headerCardWithContent : styles.headerCardStandalone))),
      children: [/*#__PURE__*/_jsx("div", {
        ...(isRenderable(icon) ? mergeProps(themeProps('banner-icon', {
          status
        }), {
          className: "x78zum5 x6s0dn4 x2lah0s"
        }) : {
          className: "x78zum5 x6s0dn4 x2lah0s"
        }),
        "aria-hidden": "true",
        children: isRenderable(icon) ? icon : defaultIconName != null ?
        /*#__PURE__*/
        // Applied to the status <Icon> itself rather than the wrapper, so
        // the element that paints the glyph is the element a theme
        // targets — a 'banner-icon' 'status:X' color override beats the
        // Icon's own variant from @layer astryx-theme (#4166).
        _jsx(Icon, {
          icon: defaultIconName,
          size: "md",
          color: iconColor,
          ...themeProps('banner-icon', {
            status
          })
        }) : null
      }), /*#__PURE__*/_jsxs("div", {
        ...{
          className: "x78zum5 xdt5ytf xxhr3t x98rzlu xeuugli"
        },
        children: [/*#__PURE__*/_jsx("div", {
          ...{
            className: "x1ghz6dp xjb2p0i xcr08ib x2mo6ok x1kq96og x1tgivj0 xj0a0fe"
          },
          children: title
        }), isRenderable(description) && /*#__PURE__*/_jsx("div", {
          ...{
            className: "x1ghz6dp xjb2p0i x141an7d x1sodnla x1ltkj2j xv1l7n4 xj0a0fe"
          },
          children: description
        })]
      }), showEndArea && /*#__PURE__*/_jsxs("div", {
        ...stylex.props(styles.endArea, edgeCompSlot.inset(spacingVars['--spacing-2'])),
        children: [endContent, hasChildren && /*#__PURE__*/_jsx(Button, {
          variant: "ghost",
          size: "sm",
          label: isExpanded ? t('@astryx.banner.collapse') : t('@astryx.banner.expand'),
          tooltip: isExpanded ? t('@astryx.banner.collapse') : t('@astryx.banner.expand'),
          icon: /*#__PURE__*/_jsx(Icon, {
            icon: "chevronDown",
            size: "sm",
            color: "inherit",
            xstyle: [styles.chevron, isExpanded && styles.chevronExpanded]
          }),
          onClick: handleToggleExpand,
          "aria-expanded": isExpanded,
          "aria-controls": showContent ? contentId : undefined,
          isIconOnly: true
        }), isDismissable && /*#__PURE__*/_jsx(Button, {
          variant: "ghost",
          size: "sm",
          label: t('@astryx.banner.dismiss'),
          tooltip: t('@astryx.banner.dismiss'),
          icon: /*#__PURE__*/_jsx(Icon, {
            icon: "close",
            size: "sm",
            color: "inherit"
          }),
          onClick: handleDismiss,
          isIconOnly: true
        })]
      })]
    }), showContent && /*#__PURE__*/_jsx("div", {
      id: contentId,
      ...mergeProps(themeProps('banner-content', {
        container,
        status
      }), {
        0: {
          className: "x1de1mus x8o8v82 x1pzlopt xgbv0en xw8tdv1 x92x3c3 x1t7ytsu x18b5jzi x1q0q8m5 x1j92z86 x1gejf6u xw8gpjh"
        },
        1: {
          className: "x1de1mus x8o8v82 x1pzlopt xgbv0en xw8tdv1 x92x3c3 x1t7ytsu x18b5jzi x1q0q8m5 x1j92z86 x1gejf6u xw8gpjh x1k5g1gk x1eprgri"
        }
      }[!!isCard << 0]),
      children: children
    })]
  });
}
Banner.displayName = 'Banner';